import { useRouteParams } from "@vueuse/router";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import {
  useCurrentExperienceVenuesQuery,
  EXPERIENCE_VENUES_QUERY_KEY,
} from "@/features/experience-shared/queries/useExperienceVenuesQuery";
import { useCurationVenues } from "@/features/experience-curation/sections/location/venues/useCurationVenues";
import type { ExperienceVenues } from "@/types/generated/MetadataExperiencesApi";

export function useSaveVenuesMutation() {
  const experienceId = useRouteParams<string>("id");
  const queryClient = useQueryClient();
  const metadataService = useMetadataExperienceApi();
  const venuesQuery = useCurrentExperienceVenuesQuery();
  const venuesStore = useCurationVenues();

  return useMutation({
    mutationFn: async () => {
      if (!venuesStore.hasChanges) return;

      const id = experienceId.value;
      const existingId = venuesQuery.data.value?.id;
      const venueCodes = venuesStore.model;

      if (venueCodes.length === 0 && existingId) {
        await metadataService.deleteVenues(existingId);
        return;
      }

      if (venueCodes.length === 0) return;

      const payload: ExperienceVenues = {
        experience_id: id,
        venues: venueCodes,
      };

      if (existingId) {
        await metadataService.updateVenues(id, existingId, payload);
      } else {
        await metadataService.createVenues(id, payload);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPERIENCE_VENUES_QUERY_KEY] }),
  });
}
